import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useCallback, useEffect, useState, type MouseEvent } from 'react';

import type { ChatSession } from '../lib/api/types';
import { useDeleteChatMutation, useUpdateChatMutation } from '../lib/api/useProjectChats';

interface ProjectChatRowMenuProps {
  chat: ChatSession;
  /**
   * Called after a successful delete — typically the parent uses this to
   * route the user away from the now-deleted chat (e.g. navigate back to
   * the project home). Optional: omit when the row is in the chats list
   * and the cache invalidation alone is enough.
   */
  onDeleted?: () => void;
}

/**
 * Kebab menu for a single chat row the user owns. Two actions: Rename and
 * Delete. Each opens a small dialog. Clicks on the kebab or inside the
 * dialogs do **not** bubble — the parent row is typically a
 * ``ListItemButton`` that would otherwise route into the chat.
 *
 * Owner-gating is the caller's responsibility: render this only when the
 * row belongs to ``user_info.user_id``. Team-shared (``project_readonly``)
 * rows have no kebab.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const ProjectChatRowMenu = ({ chat, onDeleted }: ProjectChatRowMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const updateMutation = useUpdateChatMutation();
  const deleteMutation = useDeleteChatMutation();

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleRenameClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setRenameOpen(true);
      handleMenuClose();
    },
    [handleMenuClose]
  );

  const handleDeleteClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setDeleteOpen(true);
      handleMenuClose();
    },
    [handleMenuClose]
  );

  return (
    <>
      <Tooltip title="Chat options">
        <IconButton
          size="small"
          edge="end"
          onClick={handleMenuOpen}
          aria-label="Chat options"
          data-testid={`project-chats-row-${chat.id}-menu-trigger`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        data-testid={`project-chats-row-${chat.id}-menu`}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem onClick={handleRenameClick} data-testid={`project-chats-row-${chat.id}-rename`}>
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} data-testid={`project-chats-row-${chat.id}-delete`}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
      <RenameChatDialog
        open={renameOpen}
        chat={chat}
        isPending={updateMutation.isPending}
        onClose={() => setRenameOpen(false)}
        onSubmit={async (title) => {
          await updateMutation.mutateAsync({ chatId: chat.id, body: { title } });
          setRenameOpen(false);
        }}
      />
      <DeleteChatConfirmDialog
        open={deleteOpen}
        chat={chat}
        isPending={deleteMutation.isPending}
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          await deleteMutation.mutateAsync({ chatId: chat.id, projectId: chat.projectId });
          setDeleteOpen(false);
          onDeleted?.();
        }}
      />
    </>
  );
};

interface RenameChatDialogProps {
  open: boolean;
  chat: ChatSession;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (title: string) => Promise<void>;
}

const RenameChatDialog = ({ open, chat, isPending, onClose, onSubmit }: RenameChatDialogProps) => {
  const [value, setValue] = useState(chat.title ?? '');

  // Sync local state with the latest title each time the dialog opens —
  // covers the case where the chat was renamed elsewhere between opens.
  useEffect(() => {
    if (open) setValue(chat.title ?? '');
  }, [open, chat.title]);

  const handleSubmit = useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const trimmed = value.trim();
      if (!trimmed || isPending) return;
      await onSubmit(trimmed);
    },
    [value, isPending, onSubmit]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={`project-chats-row-${chat.id}-rename-dialog`}
      onClick={(event) => event.stopPropagation()}
    >
      <DialogTitle>Rename chat</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Chat title"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={isPending}
          inputProps={{ maxLength: 200, 'data-testid': 'project-chats-rename-input' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isPending || value.trim().length === 0}
          data-testid={`project-chats-row-${chat.id}-rename-submit`}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteChatConfirmDialogProps {
  open: boolean;
  chat: ChatSession;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteChatConfirmDialog = ({
  open,
  chat,
  isPending,
  onClose,
  onConfirm,
}: DeleteChatConfirmDialogProps) => {
  const handleConfirm = useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (isPending) return;
      await onConfirm();
    },
    [isPending, onConfirm]
  );

  const titleForLabel = chat.title?.trim() || 'this chat';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid={`project-chats-row-${chat.id}-delete-dialog`}
      onClick={(event) => event.stopPropagation()}
    >
      <DialogTitle>Delete chat?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`"${titleForLabel}" will be removed from your chats. The chat row is
            soft-deleted so the project's activity feed still has its history.`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isPending}
          data-testid={`project-chats-row-${chat.id}-delete-submit`}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
